import React, { ComponentType } from 'react';
import App, { AppContext } from 'next/app';
import Head from 'next/head';
import { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { ApolloProvider } from '@apollo/client/react';
import { NextPageContext } from 'next';
import createApolloClient from '../lib/apolloClient/apolloClient';

// On the client, we store the Apollo Client in the following variable.
// This prevents the client from reinitializing between page transitions.
let globalApolloClient: ApolloClient<NormalizedCacheObject> | null = null;

interface ApolloContext extends NextPageContext {
  apolloClient?: ApolloClient<NormalizedCacheObject>;
  apolloState?: NormalizedCacheObject;
  ctx?: ApolloContext;
  AppTree?: React.ComponentType<unknown>;
}

/**
 * Installs the Apollo Client on NextPageContext
 * or NextAppContext. Useful if you want to use apolloClient
 * inside getStaticProps, getStaticPaths or getServerSideProps
 */
export const initOnContext = (ctx: ApolloContext): ApolloContext => {
  const inAppContext = Boolean(ctx.ctx);

  // We consider installing `withApollo({ ssr: true })` on global App level
  // as antipattern since it disables project wide Automatic Static Optimization.
  if (process.env.NODE_ENV === 'development') {
    if (inAppContext) {
      console.warn(
        'Warning: You have opted-out of Automatic Static Optimization due to `withApollo` in `pages/_app`.\n' +
          'Read more: https://err.sh/next.js/opt-out-auto-static-optimization\n',
      );
    }
  }

  // Initialize ApolloClient if not already done
  const apolloClient =
    ctx.apolloClient ||
    initApolloClient(ctx.apolloState || {}, inAppContext ? ctx.ctx : ctx);

  // We send the Apollo Client as a prop to the component to avoid calling initApollo() twice in the server.
  // Otherwise, the component would have to call initApollo() again but this
  // time without the context. Once that happens, the following code will make sure we send
  // the prop as `null` to the browser.
  apolloClient.toJSON = () => null;

  // Add apolloClient to NextPageContext & NextAppContext.
  // This allows us to consume the apolloClient inside our
  // custom `getInitialProps({ apolloClient })`.
  ctx.apolloClient = apolloClient;
  if (inAppContext && ctx.ctx) {
    ctx.ctx.apolloClient = apolloClient;
  }

  return ctx;
};

/**
 * Always creates a new apollo client on the server
 * Creates or reuses apollo client in the browser.
 */
export const initApolloClient = (
  initialState: NormalizedCacheObject,
  ctx?: NextPageContext
): ApolloClient<NormalizedCacheObject> => {
  // Make sure to create a new client for every server-side request so that data
  // isn't shared between connections (which would be bad)
  if (typeof window === 'undefined') {
    return createApolloClient(initialState, ctx);
  }

  // Reuse client on the client-side
  if (!globalApolloClient) {
    globalApolloClient = createApolloClient(initialState, ctx);
  }

  return globalApolloClient;
};

interface WithApolloOptions {
  ssr?: boolean;
}

interface WithApolloProps {
  apolloClient?: ApolloClient<NormalizedCacheObject>;
  apolloState?: NormalizedCacheObject;
  [key: string]: unknown;
}

/**
 * Creates a withApollo HOC
 * that provides the apolloContext
 * to a next.js Page or AppTree.
 */
export const withApollo = ({ ssr = false }: WithApolloOptions = {}) =>
  <P extends object>(PageComponent: ComponentType<P>) => {
    const WithApollo = ({ apolloClient, apolloState, ...pageProps }: WithApolloProps) => {
      let client: ApolloClient<NormalizedCacheObject>;
      if (apolloClient) {
        // Happens on: getDataFromTree & next.js ssr
        client = apolloClient;
      } else {
        // Happens on: next.js csr
        client = initApolloClient(apolloState || {}, undefined);
      }

      return (
        <ApolloProvider client={client}>
          <PageComponent {...(pageProps as P)} />
        </ApolloProvider>
      );
    };

    // Set the correct displayName in development
    if (process.env.NODE_ENV !== 'production') {
      const displayName =
        (PageComponent as { displayName?: string; name?: string }).displayName ||
        PageComponent.name ||
        'Component';
      WithApollo.displayName = `withApollo(${displayName})`;
    }

    if (ssr || (PageComponent as { getInitialProps?: unknown }).getInitialProps) {
      (WithApollo as { getInitialProps?: (ctx: ApolloContext) => Promise<WithApolloProps> }).getInitialProps = async (
        ctx: ApolloContext
      ): Promise<WithApolloProps> => {
        const inAppContext = Boolean(ctx.ctx);
        const { apolloClient } = initOnContext(ctx);

        // Run wrapped getInitialProps methods
        let pageProps: Record<string, unknown> = {};
        if ((PageComponent as { getInitialProps?: (ctx: unknown) => Promise<Record<string, unknown>> }).getInitialProps) {
          pageProps = await (PageComponent as { getInitialProps: (ctx: unknown) => Promise<Record<string, unknown>> }).getInitialProps(ctx);
        } else if (inAppContext) {
          pageProps = await App.getInitialProps(ctx as unknown as AppContext);
        }

        // Only on the server:
        if (typeof window === 'undefined') {
          const { AppTree } = ctx;
          // When redirecting, the response is finished.
          // No point in continuing to render
          if (ctx.res && ctx.res.finished) {
            return pageProps;
          }

          // Only if dataFromTree is enabled
          if (ssr && AppTree) {
            try {
              const { getDataFromTree } = await import('@apollo/client/react/ssr');

              // Since AppComponents and PageComponents have different context types
              // we need to modify their props a little.
              let props: Record<string, unknown>;
              if (inAppContext) {
                props = { ...pageProps, apolloClient };
              } else {
                props = { pageProps: { ...pageProps, apolloClient } };
              }

              // Take the Next.js AppTree, determine which queries are needed to render,
              // and fetch them.
              await getDataFromTree(<AppTree {...props} />);
            } catch (error) {
              // Prevent Apollo Client GraphQL errors from crashing SSR.
              console.error('Error while running `getDataFromTree`', error);
            }

            // getDataFromTree does not call componentWillUnmount
            // head side effect therefore need to be cleared manually
            Head.rewind();
          }
        }

        return {
          ...pageProps,
          // Extract query data from the Apollo store
          apolloState: apolloClient?.cache.extract(),
          // Provide the client for ssr. As soon as this payload
          // gets JSON.stringified it will remove itself.
          apolloClient: ctx.apolloClient,
        };
      };
    }

    return WithApollo;
  };
